import { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScanLine, Camera, X } from "lucide-react";

interface BarcodeScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScan: (code: string) => void;
}

export function BarcodeScannerDialog({ open, onOpenChange, onScan }: BarcodeScannerDialogProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const containerId = "barcode-scanner-container";

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch {
        // already stopped
      }
      scannerRef.current.clear();
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const startScanner = useCallback(async () => {
    setError(null);
    try {
      const scanner = new Html5Qrcode(containerId);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 300, height: 100 },
          aspectRatio: 2,
        },
        (decodedText) => {
          onScan(decodedText);
          stopScanner();
          onOpenChange(false);
        },
        () => {
          // ignore scan failures (no code in frame)
        }
      );
      setScanning(true);
    } catch (err: any) {
      if (err?.toString().includes("NotAllowedError") || err?.toString().includes("Permission")) {
        setError("Camera permission denied. Please allow camera access in your browser settings.");
      } else if (err?.toString().includes("NotFoundError")) {
        setError("No camera found on this device.");
      } else {
        setError("Failed to start camera. Try opening in a new tab for full hardware access.");
      }
    }
  }, [onScan, onOpenChange, stopScanner]);

  useEffect(() => {
    if (open) {
      // Small delay to ensure DOM element is rendered
      const timer = setTimeout(() => startScanner(), 300);
      return () => clearTimeout(timer);
    } else {
      stopScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) stopScanner();
        onOpenChange(val);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5" /> Scan Student Barcode
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Point your camera at any student barcode (Administration, Academic, Examination, or Accounting) to look up their profile.
          </p>

          <div
            id={containerId}
            className="w-full rounded-lg overflow-hidden border bg-black min-h-[200px]"
          />

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
              <Button
                variant="outline"
                size="sm"
                className="mt-2 w-full"
                onClick={() => startScanner()}
              >
                <Camera className="h-4 w-4 mr-1" /> Retry
              </Button>
            </div>
          )}

          {scanning && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground animate-pulse">
              <ScanLine className="h-4 w-4" />
              Scanning... Position barcode within the frame
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
